//src/components/BotSettings/AmountsSection.tsx
import React from 'react';
import { motion } from 'framer-motion';
import { Wallet, DollarSign } from 'lucide-react';
import { cn } from '../../utils/cn';

export const AmountsSection: React.FC<any> = (props) => {
  // DINAMINĖ VALIUTA (Jokių fiksuotų tekstų)
  const quoteCurrency = props.quoteCurrency || props.quote_currency || '';
  const selectedCoins = Array.isArray(props.selectedCoins) ? props.selectedCoins : [];

  // Viskas skaitoma TIESIAI iš props (Identinis modelis kaip BuyStrategy)
  const isFixed = props.useFixedAmount === true || props.use_fixed_amount === true || props.amounts?.useFixedAmount === true;
  const accBal = props.accountBalance ?? props.account_balance ?? props.amounts?.accountBalance ?? '';
  const minAmt = props.minAmount ?? props.min_amount ?? props.amounts?.minAmount ?? '';
  const pctAmt = props.percentageAmount ?? props.percentage_amount ?? props.amounts?.percentageAmount ?? 10;
  const maxAlloc = props.maxAllocation ?? props.max_allocation ?? props.amounts?.maxAllocation ?? '';

  // FUNKCIJA IDENTIŠKA JŪSŲ BuyStrategy.tsx
  const pushChange = (updates: Record<string, any>) => {
    if (props.onUpdate) {
      props.onUpdate({
        ...updates,
        amounts: { ...(props.amounts || {}), ...updates }
      });
    }
    if ((window as any).sendChange) {
      Object.entries(updates).forEach(([k, v]) => (window as any).sendChange(k, v));
    }
  };

  const handleToggleFixed = (e: React.MouseEvent) => {
    e.preventDefault();
    const val = !isFixed;
    if (props.onUseFixedAmountChange) props.onUseFixedAmountChange(val);
    pushChange({ useFixedAmount: val, use_fixed_amount: val });
  };

  const handleChange = (localKey: string, globalKey: string, val: any, propCallback?: any) => {
    let parsed: any = val;
    if (val !== '' && val !== '-') {
      const num = parseFloat(val);
      if (!isNaN(num)) parsed = num;
    }
    if (propCallback) propCallback(parsed);
    pushChange({ [localKey]: parsed, [globalKey]: parsed });
  };

  const numericAccBal = parseFloat(String(accBal)) || 0;
  const numericPctAmt = parseFloat(String(pctAmt !== '' ? pctAmt : 10)) || 0;
  const numericMinAmt = parseFloat(String(minAmt)) || 0;

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="space-y-8">
      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg border border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Amounts</h2>
            <p className="text-gray-600 dark:text-gray-400 mt-1">Configure position sizing and allocation limits</p>
          </div>
          <div className="h-12 w-12 bg-green-100 dark:bg-green-900/20 rounded-lg flex items-center justify-center">
            <Wallet className="h-6 w-6 text-green-500" />
          </div>
        </div>

        <div className="space-y-6">
          <div className="space-y-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Position Sizing</h3>

            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Use fixed amount per trade</label>
              <button type="button" onClick={handleToggleFixed} className={cn("relative inline-flex h-6 w-11 items-center rounded-full transition-colors", isFixed ? "bg-blue-500" : "bg-gray-200 dark:bg-gray-700")}>
                <span className={cn("inline-block h-4 w-4 transform rounded-full bg-white transition-transform", isFixed ? "translate-x-6" : "translate-x-1")} />
              </button>
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Account Balance</label>
              <div className="relative">
                <input
                  type="number" step="0.01" min="0" value={accBal}
                  onChange={(e) => handleChange('accountBalance', 'account_balance', e.target.value, props.onAccountBalanceChange)}
                  className="w-full px-4 py-2 pl-8 rounded-lg border bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 border-gray-300 dark:border-gray-600"
                  placeholder="Enter account balance"
                />
                <DollarSign className="absolute left-2.5 top-2.5 h-5 w-5 text-gray-400" />
              </div>
            </div>

            {isFixed ? (
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Fixed Amount per Trade</label>
                <div className="relative">
                  <input
                    type="number" step="0.01" min="0" value={minAmt}
                    onChange={(e) => handleChange('minAmount', 'min_amount', e.target.value, props.onMinAmountChange)}
                    className="w-full px-4 py-2 pl-8 rounded-lg border bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 border-gray-300 dark:border-gray-600"
                    placeholder="Enter fixed amount"
                  />
                  <DollarSign className="absolute left-2.5 top-2.5 h-5 w-5 text-gray-400" />
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Percentage per Trade</label>
                  <span className="text-lg font-semibold text-gray-900 dark:text-white">{pctAmt !== '' ? pctAmt : 10}%</span>
                </div>
                <input
                  type="range" min="1" max="100" value={pctAmt || 10}
                  onChange={(e) => handleChange('percentageAmount', 'percentage_amount', parseInt(e.target.value) || 0, props.onPercentageAmountChange)}
                  className="w-full h-2 rounded-lg appearance-none cursor-pointer bg-gray-200 dark:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Amount per trade: {(((parseFloat(String(pctAmt !== '' ? pctAmt : 10)) || 0) / 100) * numericAccBal).toFixed(2)} {quoteCurrency}
                </p>
              </div>
            )}

            <div className="space-y-2 pt-4 border-t border-gray-200 dark:border-gray-700">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Maximum Total Allocation</label>
              <div className="relative">
                <input
                  type="number" step="0.01" min="0" value={maxAlloc}
                  onChange={(e) => handleChange('maxAllocation', 'max_allocation', e.target.value, props.onMaxAllocationChange)}
                  className="w-full px-4 py-2 pl-8 rounded-lg border bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 border-gray-300 dark:border-gray-600"
                  placeholder="Enter maximum allocation"
                />
                <DollarSign className="absolute left-2.5 top-2.5 h-5 w-5 text-gray-400" />
              </div>
            </div>
          </div>

          {selectedCoins.length > 0 && (
            <div className="p-4 bg-green-50 dark:bg-green-900/10 rounded-lg border border-green-200 dark:border-green-800/30">
              <div className="flex items-start gap-3">
                <div className="h-5 w-5 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"><div className="w-2 h-2 bg-white rounded-full"></div></div>
                <div>
                  <p className="text-sm font-medium text-green-800 dark:text-green-400 mb-1">Portfolio Configuration</p>
                  <div className="text-sm text-green-700 dark:text-green-300 space-y-1">
                    <p>&#8226; Trading pairs: {selectedCoins.length} cryptocurrencies</p>
                    {quoteCurrency && <p>&#8226; Quote currency: {quoteCurrency}</p>}
                    <p>&#8226; Position sizing: {isFixed ? `Fixed ${numericMinAmt} ${quoteCurrency}` : `${numericPctAmt}% of ${numericAccBal} ${quoteCurrency}`}</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default AmountsSection;